import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography, Paper, CircularProgress } from '@mui/material';
import axios from 'axios';

// API Base URL from environment
const API_BASE = `${process.env.REACT_APP_API_BASE_URL}/api/simulation/live/data/`;

/**
 * Live Simulation Tab
 * Shows real-time floor map and machine usage
 */
const LiveSimulationTab = ({ siteId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Default site if none selected (1 = MBF Admin/Simulated Site)
    const activeSiteId = siteId || 1;

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(`${API_BASE}?site_id=${activeSiteId}`);
                setData(response.data);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching live simulation data:", err);
            }
        };

        fetchData();
        const interval = setInterval(fetchData, 2000); // Update every 2 seconds
        return () => clearInterval(interval);
    }, [activeSiteId]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                    🔴 Live Lab Feed: {data?.site_name}
                </Typography>
                <Box sx={{ bgcolor: '#ffebee', px: 2, py: 1, borderRadius: '8px', border: '1px solid #ffcdd2' }}>
                    <Typography sx={{ color: '#c62828', fontWeight: 700 }}>
                        {data?.occupancy || 0} People in Lab
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Left: Floor Map (CSS-based) */}
                <Grid item xs={12} lg={8}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 0,
                            borderRadius: '24px',
                            border: '1px solid #e6e6e6',
                            bgcolor: '#f8f9fa',
                            height: '500px',
                            position: 'relative',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <Box sx={{ p: 3, pb: 1, display: 'flex', justifyContent: 'space-between', zIndex: 20 }}>
                            <Typography sx={{ fontWeight: 600, color: '#666' }}>Digital Twin: 2D Floor View</Typography>
                            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#ff4444', bgcolor: '#ffebeb', px: 1, py: 0.5, borderRadius: '4px' }}>
                                ● LIVE SIMULATION
                            </Typography>
                        </Box>

                        {/* The CSS Floor Plan */}
                        <Box
                            sx={{
                                flex: 1,
                                position: 'relative',
                                m: 3,
                                mt: 0,
                                bgcolor: '#fff',
                                backgroundImage:
                                    'linear-gradient(#f0f0f0 1.5px, transparent 1.5px), linear-gradient(90deg, #f0f0f0 1.5px, transparent 1.5px)',
                                backgroundSize: '30px 30px',
                                border: '4px solid #333',
                                borderRadius: '4px',
                                boxShadow: 'inset 0 0 0 2px #e0e0e0', // Inner wall detail
                            }}
                        >
                            {/* --- Architectural Elements (CSS) --- */}

                            {/* Entrance Area */}
                            <Box sx={{ position: 'absolute', bottom: '-4px', left: '40%', width: '20%', height: '8px', bgcolor: '#f8f9fa', borderLeft: '4px solid #333', borderRight: '4px solid #333', zIndex: 5, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '9px', color: '#999', mt: -2 }}>ENTRANCE</Typography>
                            </Box>

                            {/* Zone: 3D Printing (Top Left) */}
                            <Box sx={{ position: 'absolute', top: '20px', left: '20px', width: '30%', height: '40%', border: '2px dashed #ddd', borderRadius: '8px', bgcolor: 'rgba(0,0,0,0.02)' }}>
                                <Typography sx={{ position: 'absolute', top: '-10px', left: '10px', bgcolor: '#fff', px: 0.5, fontSize: '10px', color: '#888', fontWeight: 600 }}>3D PRINTING</Typography>
                                {/* Table */}
                                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '70%', height: '60%', bgcolor: '#e0e0e0', borderRadius: '4px', border: '1px solid #ccc' }} />
                                {/* Machines on Table */}
                                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '70%', height: '60%', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignContent: 'center', gap: 1 }}>
                                    {[1, 2, 3, 4].map(id => {
                                        const mStatus = (data?.live_machines || []).find(m => m.id === id);
                                        const isActive = mStatus?.status === 'active';
                                        const machineName = mStatus?.name || `Machine #${id}`;
                                        return (
                                            <Box key={id} sx={{ width: '20px', height: '20px', bgcolor: isActive ? '#4caf50' : '#9e9e9e', borderRadius: '2px', boxShadow: isActive ? '0 0 8px #4caf50' : 'none', transition: 'all 0.3s' }} title={machineName} />
                                        );
                                    })}
                                </Box>
                            </Box>

                            {/* Zone: Electronics (Top Right) */}
                            <Box sx={{ position: 'absolute', top: '20px', right: '20px', width: '35%', height: '40%', border: '2px dashed #ddd', borderRadius: '8px', bgcolor: 'rgba(0,0,0,0.02)' }}>
                                <Typography sx={{ position: 'absolute', top: '-10px', left: '10px', bgcolor: '#fff', px: 0.5, fontSize: '10px', color: '#888', fontWeight: 600 }}>ELECTRONICS</Typography>
                                {/* L-Shaped Table */}
                                <Box sx={{ position: 'absolute', top: '20%', right: '10%', width: '20%', height: '70%', bgcolor: '#e0e0e0', borderRadius: '4px', border: '1px solid #ccc' }} />
                                <Box sx={{ position: 'absolute', top: '20%', right: '10%', width: '70%', height: '25%', bgcolor: '#e0e0e0', borderRadius: '4px', border: '1px solid #ccc' }} />
                                {/* Machines */}
                                <Box sx={{ position: 'absolute', top: '25%', right: '15%', display: 'flex', gap: 1 }}>
                                    {[5, 6, 7].map(id => {
                                        const mStatus = (data?.live_machines || []).find(m => m.id === id);
                                        const isActive = mStatus?.status === 'active';
                                        const machineName = mStatus?.name || `Machine #${id}`;
                                        return (
                                            <Box key={id} sx={{ width: '18px', height: '18px', bgcolor: isActive ? '#2196f3' : '#9e9e9e', borderRadius: '50%', boxShadow: isActive ? '0 0 8px #2196f3' : 'none', transition: 'all 0.3s' }} title={machineName} />
                                        );
                                    })}
                                </Box>
                            </Box>

                            {/* Zone: Workbench Area (Bottom Center) */}
                            <Box sx={{ position: 'absolute', bottom: '20px', left: '15%', width: '70%', height: '35%', border: '2px dashed #ddd', borderRadius: '8px', bgcolor: 'rgba(0,0,0,0.02)' }}>
                                <Typography sx={{ position: 'absolute', top: '-10px', left: '10px', bgcolor: '#fff', px: 0.5, fontSize: '10px', color: '#888', fontWeight: 600 }}>ASSEMBLY / HEAVY MACHINES</Typography>
                                {/* Long Bench */}
                                <Box sx={{ position: 'absolute', bottom: '30%', left: '10%', width: '80%', height: '40%', bgcolor: '#d7ccc8', borderRadius: '4px', border: '1px solid #a1887f' }} />
                                {/* Machines */}
                                <Box sx={{ position: 'absolute', bottom: '30%', left: '15%', width: '70%', height: '40%', display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
                                    {[8, 9, 10, 11, 12].map(id => {
                                        const mStatus = (data?.live_machines || []).find(m => m.id === id);
                                        const isActive = mStatus?.status === 'active';
                                        const machineName = mStatus?.name || `Machine #${id}`;
                                        return (
                                            <Box key={id} sx={{ width: '24px', height: '24px', bgcolor: isActive ? '#ff9800' : '#9e9e9e', borderRadius: '4px', border: '2px solid rgba(0,0,0,0.1)', boxShadow: isActive ? '0 0 8px #ff9800' : 'none', transition: 'all 0.3s' }} title={machineName} />
                                        );
                                    })}
                                </Box>
                            </Box>

                            {/* --- Live Moving Entities --- */}

                            {/* Moving Dots (People) */}
                            {(data?.live_users || []).map((user) => (
                                <Box
                                    key={user.id}
                                    sx={{
                                        position: 'absolute',
                                        left: `${user.x}%`,
                                        top: `${user.y}%`,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        transition: 'all 2s linear', // Smooth interpolation
                                        zIndex: 100,
                                        transform: 'translate(-50%, -50%)',
                                    }}
                                >
                                    {/* The Dot */}
                                    <Box
                                        sx={{
                                            width: '14px',
                                            height: '14px',
                                            bgcolor: '#ff4444',
                                            border: '2px solid #fff',
                                            borderRadius: '50%',
                                            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                                        }}
                                    />
                                    {/* Name Tag */}
                                    <Typography
                                        sx={{
                                            mt: 0.5,
                                            fontSize: '9px',
                                            fontWeight: 700,
                                            color: '#333',
                                            bgcolor: 'rgba(255, 255, 255, 0.95)',
                                            px: 0.8,
                                            py: 0.2,
                                            borderRadius: '10px',
                                            border: '1px solid #eee',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                            whiteSpace: 'nowrap',
                                            zIndex: 101,
                                        }}
                                    >
                                        {user.name || `User_${user.id}`}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Paper>
                </Grid>

                {/* Right: Machines List */}
                <Grid item xs={12} lg={4}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: '24px',
                            border: '1px solid #e6e6e6',
                            bgcolor: '#fff',
                            height: '500px',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <Typography sx={{ fontWeight: 600, mb: 2, color: '#333' }}>Live Machine Monitor</Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>
                            {(data?.live_machines || []).map((machine) => (
                                <Box
                                    key={machine.id}
                                    sx={{
                                        p: 2,
                                        borderRadius: '12px',
                                        border: '1px solid #eee',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        bgcolor: machine.status === 'active' ? '#f0fff4' : '#fff'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: '10px',
                                            height: '10px',
                                            borderRadius: '50%',
                                            bgcolor: machine.status === 'active' ? '#4caf50' : '#bdbdbd'
                                        }}
                                    />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>{machine.name || `Machine #${machine.id}`}</Typography>
                                        <Typography sx={{ fontSize: '12px', color: '#666' }}>
                                            {machine.status === 'active' ? `Running at ${machine.load}% Load` : 'Idle'}
                                        </Typography>
                                    </Box>
                                    {machine.status === 'active' && (
                                        <CircularProgress variant="determinate" value={machine.load} size={24} thickness={5} sx={{ color: '#4caf50' }} />
                                    )}
                                </Box>
                            ))}
                            {data?.live_machines.length === 0 && (
                                <Typography sx={{ color: '#999', textAlign: 'center', mt: 5 }}>No machines registered for live feed.</Typography>
                            )}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            <style>
                {`
                    @keyframes pulse {
                        0% { transform: scale(0.5); opacity: 1; }
                        100% { transform: scale(1.5); opacity: 0; }
                    }
                `}
            </style>
        </Box>
    );
};

export default LiveSimulationTab;